// apps/web/src/features/clients/components/ClientsListPage.tsx
//
// `/clients` — the everyday client directory (`generic-ws`). Search is always
// visible in the header; only the archive scope and the admin manager filter
// are disclosed behind the Filters toggle, because search is the action this
// surface exists for (recorded as an exception in
// `apps/web/docs/ui-system/component-decision-matrix.md`).
//
// INVARIANT: manager scope is enforced server-side; the manager filter renders
// only for an admin actor (`permissions === 'ALL'`) and `useUsersList` stays
// parked otherwise, because `/users` is `users.manage`-gated and a stray 403 on
// every page load is telemetry noise.
import { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router';
import { useTranslation } from 'react-i18next';
import type { ClientListItem } from '@magimp/shared';
import { useClientsInfinite, useUsersList } from '@magimp/shared-hooks';
import {
  Banner,
  Button,
  EmptyState,
  ErrorState,
  FilterPanel,
  FilterToggleButton,
  Input,
  LoadingState,
  ToolbarActions,
  useFilterDisclosure,
} from '../../../ui/index.js';
import { errorMessage } from '../../../api/errors.js';
import { hasPermission, useAuthStore } from '../../../stores/auth.js';
import { useClientsListState, CLIENTS_SEARCH_MAX_CHARS } from '../useClientsListState.js';
import {
  rememberClientsListAnchor,
  restoreClientsListAnchor,
  takeClientsListAnchor,
  type ClientsListReturnState,
} from '../clientsListAnchor.js';
import { useCopyText } from '../useCopyText.js';
import {
  useMobileFieldStyle,
  useTouchActionStyle,
  useTouchControlHeightScopeStyle,
  useTouchControlScopeStyle,
} from '../useMobileFieldStyle.js';
import { ClientsTable } from './ClientsTable.js';
import { ClientCreateModal } from './ClientCreateModal.js';
import { ClientsSecondaryFilters } from './ClientsSecondaryFilters.js';
import { CopyFallback } from './CopyFallback.js';

export function ClientsListPage() {
  const { t } = useTranslation(['clients', 'common']);
  const navigate = useNavigate();
  const location = useLocation();
  const perms = useAuthStore((s) => s.permissions);
  const isAdmin = perms === 'ALL';
  const canManage = hasPermission(perms, 'clients.manage');
  const state = useClientsListState(isAdmin);
  const disclosure = useFilterDisclosure();
  const [createOpen, setCreateOpen] = useState(false);
  const [anchorLost, setAnchorLost] = useState(false);
  const fieldStyle = useMobileFieldStyle();
  const actionStyle = useTouchActionStyle();
  const toggleScope = useTouchControlScopeStyle();
  const retryScope = useTouchControlHeightScopeStyle();
  const { copy, failedText, dismissFailure } = useCopyText();

  const usersQ = useUsersList({}, { enabled: isAdmin });
  const listQ = useClientsInfinite(state.wireFilter);
  const rows: ClientListItem[] = useMemo(
    () => (listQ.data?.pages ?? []).flatMap((p) => p.items),
    [listQ.data],
  );

  // Return anchor: restore the row the operator opened, wherever the server's
  // `updatedAt DESC` order has since put it. A miss (cache eviction, the record
  // left the filter) is reported, never silently ignored — see clientsListAnchor.
  const restoredRef = useRef(false);
  useEffect(() => {
    if (!listQ.isSuccess || restoredRef.current) return;
    const anchored = takeClientsListAnchor(state.filterKey);
    restoredRef.current = true;
    if (anchored === null) return;
    if (!restoreClientsListAnchor(anchored)) setAnchorLost(true);
  }, [listQ.isSuccess, state.filterKey]);

  const openDetail = (c: ClientListItem) => {
    rememberClientsListAnchor({ filterKey: state.filterKey, clientId: c.id });
    // The detail page's Back must return to THIS result set, not to a bare
    // `/clients` whose different filter signature would void the anchor.
    const returnState: ClientsListReturnState = { clientsListSearch: location.search };
    navigate(`/clients/${c.id}`, { state: returnState });
  };
  const onCopyPhone = (c: ClientListItem) => {
    if (c.primaryContact) void copy(c.primaryContact.phone);
  };

  const hasRows = rows.length > 0;
  // An error while rows are on screen keeps the loaded rows and the operator's
  // position instead of replacing the directory with an error block (AC-2 /
  // AC-13) — but the recovery differs: a failed NEXT PAGE is retried with
  // `fetchNextPage`, while a failed refresh of the pages already held must be
  // retried with `refetch`. `fetchNextPage` would be a no-op for the latter.
  const appendFailed = listQ.isFetchNextPageError && hasRows;
  const refreshFailed = listQ.isRefetchError && hasRows && !appendFailed;
  const initialFailed = listQ.isError && !hasRows;
  // The rows answer the previous query while the debounce is open or while the
  // new filter is still in flight.
  const stale = hasRows && (state.qPending || (listQ.isFetching && !listQ.isFetchingNextPage));

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: 'var(--s-3)',
        minHeight: 0,
        flex: 1,
      }}
    >
      <header
        style={{ display: 'flex', gap: 'var(--s-3)', alignItems: 'center', flexWrap: 'wrap' }}
      >
        <div style={{ flex: 1, minWidth: 0 }}>
          <h1 style={{ fontSize: 'var(--fz-18)', fontWeight: 600, margin: 0 }}>
            {t('clients:title')}
          </h1>
          {listQ.isSuccess && (
            <div
              style={{ fontSize: 'var(--fz-13)', color: 'var(--fg-3)', marginTop: 'var(--s-1)' }}
            >
              {/* "Loaded N" — never "N clients": the cursor list has no total. */}
              {t('clients:list.loadedCount', { count: rows.length })}
              {listQ.isFetching && !listQ.isFetchingNextPage
                ? ` · ${t('clients:list.refreshing')}`
                : ''}
            </div>
          )}
        </div>
        <ToolbarActions>
          <Input
            type="search"
            icon="search"
            value={state.q}
            onChange={state.setQ}
            maxLength={CLIENTS_SEARCH_MAX_CHARS}
            placeholder={t('clients:filters.search.placeholder')}
            ariaLabel={t('clients:filters.search.label')}
            style={fieldStyle}
          />
          {/* WHY the wrapper: `FilterToggleButton` takes `size`, not `style`,
              and its smallest control token is 28 px — below the touch minimum
              this header's other actions reach through `useTouchActionStyle`.
              Raising the token in a scope around it leaves the shared API and
              the fine-pointer density alone. */}
          <span style={{ display: 'flex', ...toggleScope }}>
            <FilterToggleButton
              open={disclosure.open}
              activeCount={state.secondaryActiveCount}
              label={t('clients:filters.toggle')}
              aria-controls={disclosure.panelId}
              onClick={disclosure.toggle}
              data-testid="clients-filters-toggle"
            />
          </span>
          {canManage && (
            <Button
              icon="plus"
              kind="primary"
              style={actionStyle}
              onClick={() => setCreateOpen(true)}
            >
              {t('clients:actions.create')}
            </Button>
          )}
        </ToolbarActions>
      </header>

      {state.qTruncated && (
        <Banner tone="warn">
          {t('clients:filters.search.truncated', { max: CLIENTS_SEARCH_MAX_CHARS })}
        </Banner>
      )}

      {state.managerInvalid && (
        <Banner
          tone="warn"
          action={
            <Button
              size="sm"
              kind="ghost"
              style={actionStyle}
              onClick={state.dismissManagerInvalid}
            >
              {t('common:actions.close')}
            </Button>
          }
        >
          {t('clients:filters.manager.invalid')}
        </Banner>
      )}

      <FilterPanel open={disclosure.open} id={disclosure.panelId}>
        <ClientsSecondaryFilters
          archived={state.archived}
          onArchived={state.setArchived}
          managerId={state.managerId}
          onManagerId={state.setManagerId}
          managers={isAdmin ? (usersQ.data ?? []).map((u) => ({ id: u.id, name: u.name })) : null}
          onReset={state.resetSecondary}
        />
      </FilterPanel>

      <div role="status" aria-live="polite" style={{ minHeight: 0 }}>
        {anchorLost && (
          <Banner
            tone="info"
            action={
              <Button
                size="sm"
                kind="ghost"
                style={actionStyle}
                onClick={() => setAnchorLost(false)}
              >
                {t('common:actions.close')}
              </Button>
            }
          >
            {t('clients:list.anchorLost')}
          </Banner>
        )}
        {stale && (
          // Plain text, not a nested live region: the rows below are the
          // PREVIOUS answer, and presenting them as the current one is the
          // failure this line exists to prevent.
          <p
            data-testid="clients-stale"
            style={{ margin: 0, fontSize: 'var(--fz-12)', color: 'var(--fg-3)' }}
          >
            {t('clients:list.stale')}
          </p>
        )}
      </div>

      {failedText !== null && <CopyFallback text={failedText} onDismiss={dismissFailure} />}

      {initialFailed && (
        // WHY the wrapper: `ErrorState` renders its own retry `Button` and
        // takes no `style`, so the only recovery from an empty list stayed at
        // the desktop 32 px on a phone. The scope raises the token that button
        // reads; the shared primitive and desktop density are untouched.
        <div style={retryScope}>
          <ErrorState
            title={t('common:states.loadError')}
            text={errorMessage(listQ.error)}
            onRetry={() => void listQ.refetch()}
          />
        </div>
      )}
      {listQ.isPending && <LoadingState />}
      {hasRows || listQ.isSuccess ? (
        <>
          <ClientsTable
            rows={rows}
            onSelect={openDetail}
            onCopyPhone={onCopyPhone}
            filterKey={state.filterKey}
            showManager={isAdmin}
            emptyState={<ClientsEmptyState state={state} />}
          />
          {appendFailed && (
            <Banner
              tone="error"
              action={
                <Button
                  size="sm"
                  style={actionStyle}
                  onClick={() => void listQ.fetchNextPage()}
                  disabled={listQ.isFetchingNextPage}
                  data-testid="clients-append-retry"
                >
                  {t('common:actions.retry')}
                </Button>
              }
            >
              {t('clients:list.appendFailed')}
            </Banner>
          )}
          {refreshFailed && (
            <Banner
              tone="error"
              action={
                <Button
                  size="sm"
                  style={actionStyle}
                  onClick={() => void listQ.refetch()}
                  disabled={listQ.isFetching}
                  data-testid="clients-refresh-retry"
                >
                  {t('common:actions.retry')}
                </Button>
              }
            >
              {t('clients:list.refreshFailed')}
            </Banner>
          )}
          {listQ.hasNextPage && !appendFailed && (
            <div style={{ display: 'flex', justifyContent: 'center', padding: 'var(--s-3)' }}>
              <Button
                style={actionStyle}
                onClick={() => void listQ.fetchNextPage()}
                disabled={listQ.isFetchingNextPage}
                data-testid="clients-load-more"
              >
                {listQ.isFetchingNextPage
                  ? t('common:states.loading')
                  : t('clients:actions.loadMore')}
              </Button>
            </div>
          )}
        </>
      ) : null}

      {createOpen && <ClientCreateModal onClose={() => setCreateOpen(false)} />}
    </div>
  );
}

/**
 * Truthful empty copy: a first-use directory, a search that matched nothing and
 * an empty archive are three different situations with three different
 * recoveries (AC-13).
 */
function ClientsEmptyState({ state }: { state: ReturnType<typeof useClientsListState> }) {
  const { t } = useTranslation(['clients', 'common']);
  const searching = state.wireFilter.q !== undefined || !!state.managerId;
  if (searching) {
    return (
      <EmptyState
        title={t('clients:empty.noResultsTitle')}
        text={t('clients:empty.noResultsText')}
      />
    );
  }
  if (state.archived === 'archived') {
    return (
      <EmptyState title={t('clients:empty.archiveTitle')} text={t('clients:empty.archiveText')} />
    );
  }
  return (
    <EmptyState title={t('clients:empty.firstUseTitle')} text={t('clients:empty.firstUseText')} />
  );
}
